import { useCallback, useEffect, useRef, useState } from "react";
import { useMutations } from "./useApiMutation";

/**
 * Cosmetic labels/colors for the platform's LLM providers. The provider *key* is the
 * value the backend stores on an agent and looks the API key up by (LlmModels.Provider),
 * so it must match the backend spelling exactly — only the label is free text.
 */
export const PROVIDER_DISPLAY = {
  OpenAI: { color: "green", label: "OpenAI" },
  Claude: { color: "purple", label: "Anthropic Claude" },
  Vision: { color: "blue", label: "Google Vision" },
  Gemini: { color: "cyan", label: "Google Gemini" },
  Grok: { color: "volcano", label: "xAI Grok" },
  DeepSeek: { color: "blue", label: "DeepSeek" },
  Copilot: { color: "geekblue", label: "Microsoft Copilot" },
  Ollama: { color: "gold", label: "Ollama Cloud" },
  Kimi: { color: "magenta", label: "Moonshot Kimi" },
};

export const providerMeta = (provider) =>
  PROVIDER_DISPLAY[provider] || { color: "default", label: provider };

// Used only when the providers call fails — without it the form's provider field
// would render empty and an admin could not save an agent at all.
const FALLBACK = ["OpenAI", "Claude", "Vision"];

/**
 * Lists the providers the platform actually has models registered for, so the agent
 * forms stay in step with the LLM Models / LLM Keys screens instead of a hardcoded list
 * that silently goes stale every time a provider is added.
 */
export function useLlmProviders() {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchLlmProviderOptions", {
        method: "get",
        endpoint: "/v1/llm-models/providers",
      });
      const rows = data?.data || [];
      setProviders(rows.length ? rows.map((r) => r.provider) : FALLBACK);
    } catch {
      setProviders(FALLBACK);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const options = providers.map((p) => ({
    value: p,
    label: providerMeta(p).label,
  }));

  return { providers, options, loading, reload: load };
}
